import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type {
  CompleteSignupRequest,
  CompleteSignupResponse,
  GoogleAuthRequest,
  GoogleAuthResponse,
  Me,
  RequestCodeRequest,
  RequestCodeResponse,
  SuperadminLoginRequest,
  SuperadminLoginResponse,
  UserRole,
  VerifyCodeRequest,
  VerifyCodeResponse,
  WorkspaceLoginRequest,
  WorkspaceLoginResponse,
} from "@/lib/api-types";

const ME_KEY = ["auth", "workspace-me"] as const;
const SA_KEY = ["auth", "superadmin-session"] as const;

// Persist the last-known identity so a cold load / hard refresh can seed the
// `me` query synchronously (via initialData) and render the app shell WITHOUT
// the blank AuthLoading screen. It's revalidated in the background every mount.
// Not a security boundary — auth is the httpOnly cookie; this is just profile
// data for instant paint, cleared on logout.
const ME_CACHE_KEY = "editlock:me";
function readCachedMe(): Me | null {
  try {
    const raw = localStorage.getItem(ME_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}
function writeCachedMe(me: Me | null): void {
  try {
    if (me) localStorage.setItem(ME_CACHE_KEY, JSON.stringify(me));
    else localStorage.removeItem(ME_CACHE_KEY);
  } catch {
    /* private mode / disabled storage — degrade to the fetch path */
  }
}

// ── Workspace user (ADMIN / FULL_ACCESS / EDIT_SHARE / EDIT) ─────────────────

/**
 * GET /api/editor/me — works for every workspace role (backend gates
 * `editor.js` behind WORKSPACE_ROLES). Returns null on 401/403.
 */
export function useMe() {
  return useQuery<Me | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const me = await api.get<Me>("/editor/me");
        writeCachedMe(me);
        return me;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          writeCachedMe(null);
          return null;
        }
        throw err;
      }
    },
    // Seed synchronously from the last-known identity so the guard has data on
    // the first render (isLoading = false) and skips AuthLoading. updatedAt = 0
    // forces an immediate background revalidate so stale identity self-corrects.
    initialData: () => readCachedMe() ?? undefined,
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * POST /api/auth/login — workspace login. Body is { email, password }.
 * Response includes `mustChangePassword` and `role` but NOT the full Me —
 * we invalidate the me query so useMe() refetches with the fresh cookie.
 */
export function useWorkspaceLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceLoginRequest) =>
      api.post<WorkspaceLoginResponse>("/auth/login", body),
    onSuccess: (res) => {
      // Prime the cache so the route guard has identity immediately (no blank
      // AuthLoading screen), then refresh in the background.
      qc.setQueryData(ME_KEY, res.user);
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

// ── Self-serve auth: email OTP + Google ──────────────────────────────────────

/** POST /api/auth/request-code — email a 6-digit sign-in code. Always ok. */
export function useRequestCode() {
  return useMutation({
    mutationFn: (body: RequestCodeRequest) =>
      api.post<RequestCodeResponse>("/auth/request-code", body),
  });
}

/**
 * POST /api/auth/verify-code — validate the code. Existing user → logged in
 * (cookie set), so we invalidate the me cache. New email → returns a
 * signupToken (no session yet), so nothing to invalidate.
 */
export function useVerifyCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VerifyCodeRequest) =>
      api.post<VerifyCodeResponse>("/auth/verify-code", body),
    onSuccess: (res) => {
      if (!("signupToken" in res)) {
        qc.setQueryData(ME_KEY, res.user);
        qc.invalidateQueries({ queryKey: ME_KEY });
      }
    },
  });
}

/**
 * POST /api/auth/complete-signup — set the password for a new OTP account.
 * Creates the workspace ADMIN and logs in; invalidate me so useMe() refetches.
 */
export function useCompleteSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompleteSignupRequest) =>
      api.post<CompleteSignupResponse>("/auth/complete-signup", body),
    onSuccess: (res) => {
      qc.setQueryData(ME_KEY, res.user);
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/**
 * POST /api/auth/google — "Continue with Google". Verifies the Google ID token
 * server-side, logs in or creates the workspace ADMIN, sets the cookie.
 */
export function useGoogleAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GoogleAuthRequest) =>
      api.post<GoogleAuthResponse>("/auth/google", body),
    onSuccess: (res) => {
      qc.setQueryData(ME_KEY, res.user);
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/**
 * PATCH /api/editor/me — self-update. Currently only { name } is editable
 * (email is display-only per Phase 1 spec). Invalidates the me cache so the
 * top-right user menu and any other consumer refreshes.
 */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      api.patch<{ ok: true; name: string }>("/editor/me", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/**
 * POST /api/auth/change-password — self-service password change.
 * Body: { currentPassword, newPassword }. Backend requires currentPassword
 * for authenticated users (only the force-change flow allows omitting it).
 * Doesn't touch the me cache — password isn't returned anywhere.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: true }>("/auth/change-password", body),
  });
}

/** POST /api/auth/logout */
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>("/auth/logout"),
    onSettled: () => {
      qc.setQueryData(ME_KEY, null);
      qc.setQueryData(SA_KEY, false);
      writeCachedMe(null);
      qc.removeQueries();
    },
  });
}

// ── Superadmin (separate credentials, separate endpoints) ────────────────────

/**
 * POST /api/superadmin/login — Body is { username, password } (NOT email).
 * Sets the same `auth_token` cookie as workspace login; backend distinguishes
 * by JWT payload role: 'SUPERADMIN'.
 *
 * Known conflict from memory: workspace + superadmin sessions share cookie
 * name → last login wins → 403s on the other side. UX-side we treat these
 * as mutually exclusive and don't attempt to preserve both.
 */
export function useSuperadminLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SuperadminLoginRequest) =>
      api.post<SuperadminLoginResponse>("/superadmin/login", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SA_KEY });
      qc.setQueryData(ME_KEY, null);
      writeCachedMe(null);
    },
  });
}

/** POST /api/superadmin/logout */
export function useSuperadminLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>("/superadmin/logout"),
    onSettled: () => {
      qc.setQueryData(SA_KEY, false);
      qc.removeQueries();
    },
  });
}

/**
 * Backend has no `/api/superadmin/me` endpoint. We probe a superadmin-gated
 * GET; 200 → authenticated, 401/403 → not. Cheap enough for the superadmin
 * layout to run on mount.
 */
export function useSuperadminSession() {
  return useQuery<boolean>({
    queryKey: SA_KEY,
    queryFn: async () => {
      try {
        await api.get("/superadmin/requests");
        return true;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return false;
        }
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

// ── High-level helpers ──────────────────────────────────────────────────────

/**
 * Preferred hook for anything under `/app/*` or `/admin/*` (workspace areas).
 * Not for the `/superadmin/*` area — that uses `useSuperadminSession`.
 */
export function useAuth() {
  const { data: me, isLoading, isError, error, isFetching, refetch } = useMe();
  return {
    user: me ?? null,
    role: me?.role ?? null,
    workspaceAdminId: me?.workspaceAdminId ?? null,
    workspaceName: me?.workspaceName ?? null,
    isAuthenticated: !!me,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
}

export function hasRole(user: Me | null, ...allowed: UserRole[]): boolean {
  if (!user) return false;
  return allowed.includes(user.role);
}

export function isWorkspaceAdmin(user: Me | null): boolean {
  return user?.role === "ADMIN";
}
